/**
 * An accessible dialog: the one modal pattern for the app.
 *
 * The hand-rolled modals it replaces were a `fixed` div each — no dialog role,
 * no Escape, focus left behind on the page, and several with light-only
 * colours that turned white-on-white in dark mode. This one:
 *
 * - is `role="dialog"` + `aria-modal`, labelled by its title;
 * - closes on Escape and on a click on the backdrop (unless `dismissible` is
 *   false, for a dialog that must be answered);
 * - moves focus into the dialog, keeps Tab inside it, and gives focus back to
 *   whatever opened it on close.
 */

import { X, type LucideIcon } from 'lucide-react'
import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { ICON_BUTTON } from './styles'

const SIZES = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
} as const

const FOCUSABLE =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface ModalProps {
    open: boolean
    onClose: () => void
    title: ReactNode
    description?: ReactNode
    icon?: LucideIcon
    size?: keyof typeof SIZES
    /** Buttons row at the bottom. */
    footer?: ReactNode
    dismissible?: boolean
    children?: ReactNode
}

export default function Modal({
    open,
    onClose,
    title,
    description,
    icon: Icon,
    size = 'md',
    footer,
    dismissible = true,
    children,
}: ModalProps) {
    const titleId = useId()
    const descriptionId = useId()
    const panelRef = useRef<HTMLDivElement>(null)
    const onCloseRef = useRef(onClose)
    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
        if (!open) return
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const panel = panelRef.current
        // Focus the first field if there is one, else the panel itself — not
        // the close button, which an Enter would then trigger.
        const first = panel?.querySelector<HTMLElement>('input, textarea, select') ?? panel
        first?.focus()

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && dismissible) {
                event.stopPropagation()
                onCloseRef.current()
                return
            }
            if (event.key !== 'Tab' || !panel) return
            const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)]
            if (focusable.length === 0) {
                event.preventDefault()
                return
            }
            const firstEl = focusable[0]
            const lastEl = focusable[focusable.length - 1]
            if (event.shiftKey && (document.activeElement === firstEl || document.activeElement === panel)) {
                event.preventDefault()
                lastEl.focus()
            } else if (!event.shiftKey && document.activeElement === lastEl) {
                event.preventDefault()
                firstEl.focus()
            }
        }
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('keydown', onKeyDown)
            opener?.focus?.()
        }
    }, [open, dismissible])

    if (!open) return null

    return createPortal(
        <div
            role="presentation"
            className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 p-4 backdrop-blur-xs animate-fadeIn"
            onMouseDown={(event) => {
                if (dismissible && event.target === event.currentTarget) onClose()
            }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={description ? descriptionId : undefined}
                tabIndex={-1}
                className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-raised outline-hidden animate-scaleIn ${SIZES[size]}`}
            >
                <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
                    <div className="flex min-w-0 items-start gap-3">
                        {Icon && (
                            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                                <Icon className="h-4 w-4" aria-hidden="true" />
                            </span>
                        )}
                        <div className="min-w-0">
                            <h2 id={titleId} className="text-base font-semibold text-fg-strong">
                                {title}
                            </h2>
                            {description && (
                                <p id={descriptionId} className="mt-0.5 text-sm text-fg-muted">
                                    {description}
                                </p>
                            )}
                        </div>
                    </div>
                    {dismissible && (
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Schließen"
                            className={ICON_BUTTON}
                        >
                            <X className="h-5 w-5" aria-hidden="true" />
                        </button>
                    )}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
                {footer && (
                    <div className="flex items-center justify-end gap-3 border-t border-line bg-surface-subtle px-5 py-3">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body,
    )
}
